/**
 * Provides methods for parsing and generating URL api strings.
 * This registers a route handler with the app router.
 *
 * Any time a valid URL api call is detected, a message is published of the form
 *
 * urlapi/<module>/<op>
 *
 *It will be passed all of the parameters in an object.
 *
 *
 * Url formats we have to support
 * - urlapi servlet (parse & generate):  urlapi/<module>/<op>/?<param>=<value>&<param>=<value>...
 * - external forms (parse & generate):  forms/form.jsp?pu=<scrambled>
 * - flex (parse):  application/index.html#module=<module>;op=<operation>;<param>=<value>;<param>=<value>...
 * - flex external form (parse):  application/form.jsp?su=<uid>
 *
 *
 *  Methods
 *
 *      load()
 *          Must be called to initialize url api route handling
 *      params()
 *          Returns the parameters that were found in the last handled URL API call
 *      reset()
 *          Destroy all stored info on the last url api call
 *
 *
 *   Modules/Ops
 *      dashboards
 *          dashboard
 *      main
 *          search
 *          fileupload
 *          jobqueue
 *          form
 *          folder
 *      viewer
 *          viewdoc
 *      forms (external forms)
 *          edit
 *
 *
 *      Each op has the follow interface
 *
 *      urlapi.[module].[op]
 *          apiCall(params):
 *              Returns a URL conforming to the URL API for the given operation and parameters
 *          translatedCall(params):
 *              Returns the transformed url(after the #) that the app expects to receive from the server for a URL api call
 *          open(params):
 *              Open a window using the window manager going to this call
 *          scrambledCall(params):
 *              Return a full path to a scrambled call
 *          now(params):
 *              Modifies the URL to match the given API call
 *          wasLastCalled():
 *              Return true if the URL api call last handled was this operation
 *          url:(params, isScrambled):
 *              Returns the full url to an operation with the given params. Optionally scrambles the params.
 */
import topic from "common/managers/topic";
import router from "common/router";
import AppModule from "common/enums/AppModule";
import windowManager from "common/managers/windowManager";
import scrambler from "common/managers/scrambler";
import loginProps from "common/managers/loginProps";
import shortUrlOps from "common/datasources/shortUrl/ShortUrlOperations";

/*
 Original Call:
 http://<server>:<port>/<context>/urlapi/<module>/<op>/?<param=x>&<param=y>&<etc>

 Converted Call - Desktop:
 http://<server>:<port>/<context>/app/index.html#module=<module>&op=<op>&<param=x>&<param=y>&<etc>

 Converted Call - Mobile
 http://<server>:<port>/<context>/m/index.html#module=<module>&op=<op>&<param=x>&<param=y>&<etc>



 */

/**
 * A list of known parameters that should be case-insensitive.  All are to-lowered here,
 * in order to check against these values for existence.
 */
var knownParams = [
        "module",
        "op",
        "dashboardid",
        "dashboardname",
        "categoryname",
        "formname",
        "formdesignid",
        "formid",
        "mode",
        "docid",
        "versionhistoryid",
        "batchid",
        "categoryid",
        "doctypeid",
        "menus",
        "directview",
        "showpanel",
        "viewid",
        "jobid",
        "showmarkup",
        "folderid"
    ],

    //The returned url api interface
    API = {

        /**
         *
         * @returns The parameters that were found(if any) for the URL API call that was last called
         */
        params: function() {
            return API._initial;
        },

        _initial: {},

        /**
         * Connect the URL API to the router
         */
        load: function() {
            var me = this;
            var _dispatchUrlApiEvents = function(params, queryParams) {
                API._initial = params;
                topic.publish("urlapi/" + params.module, params.op, params, queryParams);
                topic.publish("urlapi/" + params.module + "/" + params.op, params, queryParams);
                topic.subscribe("app/logoutImminent", function() {
                    router.endCall();
                });
            };

            // If there are both "module" and "op" parameters, in any order, it's a URL API call to be handled here.
            router.route(/^(?=.*module=)(?=.*op=)|(?=.*pu=).*$/, function(queryParams) {
                var params = me._parseQueryStringParams("?" + window.location.hash.substring(1).replace(/;/g, "&"));
                _dispatchUrlApiEvents(params, queryParams);
            });

            // If there is an "su" parameter, in any order on the URL, it's a short URL to be evaluated.
            router.route(/^(?=.*su=).*$/, function(queryParams) {
                // Server call to get the resolved URL, then publish urlapi topics, without redirecting on the URL
                var params = me._parseQueryStringParams("?" + window.location.hash.substring(1).replace(/;/g, "&"));
                return shortUrlOps.getUrl(params.su)
                    .done(function(data) {
                        var evaluatedParams = me._parseQueryStringParams(data.replace(/;/g, "&").replace("#", "?"));
                        _dispatchUrlApiEvents($.extend(evaluatedParams, params), queryParams);
                    });

            });
        },

        /**
         * Parse query string parameters and handle known items being converted to lowercase.
         * @param query query string hash
         * @return {*} parameter object
         * @private
         */
        _parseQueryStringParams: function(query) {

            // this first part is a replacement for kendo.parseQueryStringParams.
            // It does not properly handle duplicate parameters.
            var queryString = query.split('?')[1] || "",
                params = this.deserializeParams(queryString);

            // this next part handles our known parameter names and converts them to lowercase.
            $.each(params, function(key, value) {
                if ($.inArray(key.toLowerCase(), knownParams) !== -1) {
                    delete params[key];
                    params[key.toLowerCase()] = value;
                }
            });

            // descramble if there's a scrambled param
            if (params.pu) {
                var unscrambledParams = scrambler.descramble(params.pu);
                var evaluatedParams = this.deserializeParams(unscrambledParams.replace(/;/g, "&").replace("#", ""));

                // If there was a scrambled param, we only allow overwriting of module and op from unscrambled params.
                // (This is allowed solely due to our url api format, as the module and op can come from the endpoint, rather than as params.
                if (params.module) {
                    evaluatedParams.module = params.module;
                }
                if (params.op) {
                    evaluatedParams.op = params.op;
                }
                params = evaluatedParams;
            }

            return params;
        },

        /**
         * Converts an object to an encoded query string
         * @param params
         * @returns {string}
         */
        serializeParams: function(params) {
            function encodeParam(paramName, paramValue) {
                return encodeURIComponent(paramName) + "=" + (!!paramValue ? encodeURIComponent(paramValue) : "")
            }
            var query = [];
            for (var param in params) {
                var value = params[param];
                if (!Array.isArray(value)) {
                    query.push(encodeParam(param, value));
                } else {
                    for (var i = 0; i < value.length; i++) {
                        query.push(encodeParam(param, value[i]));
                    }
                }
            }
            return query.join("&");
        },

        /**
         * Converts an encoded query string to an object
         * @param query
         * @returns {{}}
         */
        deserializeParams: function(query) {
            var params = {},
                paramParts = query.split(/&|=/),
                length = paramParts.length;

            for (var i = 0; i < length; i += 2) {
                if (paramParts[i] !== "") {
                    var paramName = decodeURIComponent(paramParts[i]),
                        paramValue = decodeURIComponent(paramParts[i + 1]);

                    // if not already found, set it and forget it.
                    if (!params[paramName]) {
                        params[paramName] = paramValue;
                    } else {
                        // otherwise, make an array of the 2.
                        if (!Array.isArray(params[paramName])) {
                            params[paramName] = [params[paramName], paramValue];
                        } else {
                            params[paramName].push(paramValue);
                        }
                    }
                }
            }
            return params;
        },

        /**
         * Get query string for params optionally scrambling it
         * @param params {string} - The query string
         * @param isScrambled {boolean} - Whether to scramble the params
         * @param authParams {string} - The request auth params
         * @returns {string} - The query string with a leading ?
         */
        getEncodedParams(params, isScrambled, authParams) {
            const encoded = API.serializeParams(params);
            const encodedAuth = API.serializeParams(authParams);
            let ret = encodedAuth ? `?${encodedAuth}&` : "?";
            if(isScrambled) {
                return ret + "pu=" + scrambler.scramble(encoded);
            } else if(encoded.length > 0 || encodedAuth.length > 0) {
                return `${ret}${encoded}`;
            } else {
                return "";
            }
        },

        /**
         * Load the URL API from hash. Event must be triggered via caller. Force params to be set, using
         * optional param, if you know the module and op and are not relying on them being passed on the URL.
         *
         * @param hash hash
         * @param forceSet if true, will set URL API params variable, even if module and op are not passed.
         * @return string event or null when none corresponds
         */
        loadHash: function(hash, forceSet) {
            var res = $.Deferred(),
                me = this;

            // ensure hash starts with #
            if (!hash) {
                hash = "#";
            } else if (hash.indexOf("#") != 0) {
                hash = "#" + hash;
            }

            // parse params
            var params = this._parseQueryStringParams("?" + hash.substring(1));

            // look for a short URL, and evaluate it if it exists
            var def = $.Deferred();
            if (params.su) {
                shortUrlOps.getUrl(params.su)
                    .done(function(data) {
                        var evaluatedParams = me._parseQueryStringParams(data.replace(/;/g, "&").replace("#", "?"));
                        params = $.extend(evaluatedParams, params);
                        def.resolve();
                    });
            } else {
                def.resolve();
            }

            def.promise().done(function() {
                // return event name
                if ((params.module && params.op) || forceSet) {
                    API._initial = params;
                    res.resolve("urlapi/" + params.op);
                }

                // no corresponding event
                res.resolve();
            });

            return res;
        },

        /**
         * Destroy all information that shows a URL API call we handled.  An alternative to this the now() method on
         * each API operation.
         */
        reset: function() {
            API._initial = {};
        },

        /**
         * Get parameter arguments.
         *
         * @param params parameters
         * @return {Array} array of arguments
         */
        getArguments: function(params) {
            var values = [];
            if (params) {
                for (var key in params) {
                    if (key.indexOf("arg") == 0) {
                        var index = parseInt(key.substring(3));
                        if (!isNaN(index)) {
                            index = index - 1;
                            values[index] = params[key];
                        }
                    }
                }
            }
            return values;
        },

        /**
         * Gets the "dynamic" parameters from the given set.  Dynamic parameters are those which can vary
         * based on document type, form, etc - stuff we need to know, but can't code for.  Keys are usually
         * metadata short names or form control names.
         *
         * Strips out all known parameters from the given argument object.
         *
         * Does not modify the original argument.
         *
         * @param params query string params
         * @return {*}
         */
        getDynamicParameters: function(params) {
            var items = params || {};
            $.each(items, function(key) {
                if ($.inArray(key.toLowerCase(), knownParams) !== -1) {
                    delete params[key];
                }
            });
            return items;
        }

    };


/**
 * Represents a module
 * @param name Name of module(AppModule Enum)
 * @constructor
 */
var Module = function(name) {
    var details = AppModule.getDetails(name);
    this.module_name = details ? details.hash : name.toLowerCase();
    this.fullModule = name;
};

/**
 * Represents an operation
 * @param op_name Operation name
 * @param module_name Module name
 * @param fullModule AppModule Enum
 * @param {boolean} [isExternal=true] - Whether standalone op is external (only used if there is no fullModule)
 * @private
 * @constructor
 */
var Op = function(op_name, module_name, fullModule, isExternal = true) {
    this.op_name = op_name;
    this.module_name = module_name;
    this.fullModule = fullModule;
    this.isExternal = isExternal;
};

/**
 * Returns the published URL API call for this operation.
 * @param params Extra parameters
 * @returns {string}
 */
Op.prototype.apiCall = function(params) {
    var encoded = API.serializeParams(params);
    return context.path + "/urlapi/" + this.module_name + "/" + this.op_name + "/?" + encoded;
};

/**
 * Produces a full url to a url api call using the urlapi servlet and the system properties urlapi url base
 * @param params {{ name: value }} Object of name value pairs
 * @param isScrambled {boolean} default false, Whether to scramble the params or not for the created url
 * @param authParams Object of name value pairs of auth params to be added to the call
 * @returns {string} The full url to the api call
 */
Op.prototype.url = function(params, isScrambled, authParams) {
    const base = loginProps.get().urlApiBase;
    const query = API.getEncodedParams(params, isScrambled, authParams);
    let path;
    if(this.fullModule) {
        var moduleProps = AppModule.getDetails(this.fullModule);
        if(moduleProps.type === "external") {
            path = `${this.module_name}/${this.op_name}.jsp`;
        } else {
            path = `urlapi/${this.module_name}/${this.op_name}/`;
        }
    } else {
        if(this.isExternal) {
            path = `${this.op_name}.jsp`;
        } else {
            path = `urlapi/${this.op_name}/`;
        }
    }
    return `${base}/${path}${query}`;
};

/**
 * Returns the portion after # of this operation.
 * @param params Extra parameters.
 * @returns {string}
 */
Op.prototype.translatedCall = function(params) {
    var isFlex = AppModule.getDetails(this.fullModule).type == "flex";
    var sep = isFlex ? ";" : "&";
    var encoded = API.serializeParams(params).replace(/&(.+?)=/g, sep + "$1=");
    return "module=" + this.module_name + sep + "op=" + this.op_name + sep + encoded;
};

/**
 * Returns the scrambled call. Will be a link directly into the HTML Main App.
 * If support is needed for scrambled URLs into other modules, this will need to
 * be expanded.
 * @param params
 */
Op.prototype.scrambledCall = function(params) {
    var base = loginProps.get().urlApiBase;
    var pu = scrambler.scramble(this.translatedCall(params));
    return base + "/app/index.html#pu=" + pu;
};


/**
 * Open a specific URL API call in the appropriate window.  This ties into the window manager.
 * @param params Extra parameter
 * @param name Optional name of window to use
 */
Op.prototype.open = function(params, name) {
    var hash = "#" + this.translatedCall(params);
    windowManager.openModule(this.fullModule, hash, name);
};

/**
 * Navigate this window to this given operation.
 * @param params Extra paramters
 */
Op.prototype.now = function(params) {
    router.navigate(this.translatedCall(params), true);
};

/**
 * Determine if this operation was the last thing called.
 * @returns {boolean}
 */
Op.prototype.wasLastCalled = function() {
    return this.match(API._initial.module, API._initial.op);
};

/**
 * Determine if the passes in parameters match this operation.
 * @param module Module name
 * @param op Operation name
 * @returns {boolean}
 */
Op.prototype.match = function(module, op) {
    return module && op && module.toLowerCase() == this.module_name && op.toLowerCase() == this.op_name;
};


/**
 * Adds a new operation to the module with the given name
 * @private
 * @param op_name
 */
Module.prototype._addOp = function(op_name) {
    this[op_name] = new Op(op_name, this.module_name, this.fullModule);
};

//All of the different API calls that are known
API.dashboards = new Module(AppModule.DASHBOARDS);
API.dashboards._addOp("dashboard");

API.main = new Module(AppModule.MAIN_HTML);
API.main._addOp("search");
API.main._addOp("fileupload");
API.main._addOp("jobqueue");
API.main._addOp("downloadexport");
API.main._addOp("systemmessage");
API.main._addOp("form");
API.main._addOp("viewdoc");
API.main._addOp("batches");
API.main._addOp("index");
API.main._addOp("panel");
API.main._addOp("folder");
API.main._addOp("workspace");

API.viewer = new Module(AppModule.VIEWER);
API.viewer._addOp("viewdoc");
API.viewer._addOp("previewdoc");
API.viewer._addOp("editing");

API.forms = new Module(AppModule.EXTERNAL_FORMS);
API.forms._addOp("form");

API.login = new Op("login", null, null);
API.viewPdf = new Op("viewPDF", null, null, false);

export default API;
